import clsx from 'clsx';

/**
 * Serves a local raster image from the copies committed under
 * `public/responsive/`, generated by `scripts/generate-image-variants.mjs`.
 *
 * The Next image optimizer is off project-wide, so `next/image` would hand the
 * browser the full-size source — 3720px and half a megabyte for the header
 * banner. Remote images do not have this problem: the backend already stores
 * them at the size they are shown. These local files are the exception, so the
 * resizing that used to happen per request happens at build time instead, and
 * the browser picks from a plain `srcset`.
 *
 * `width` and `height` are the intrinsic dimensions of the source. They are only
 * there to give the box an aspect ratio, so nothing shifts while the file loads;
 * the rendered size comes from CSS.
 */
type ResponsiveLocalImageProps = {
    /** File name without extension, as passed to the generator script. */
    name: string;
    /** Widths that exist for this image, ascending. */
    widths: readonly number[];
    /** Slot width per breakpoint, in the usual `sizes` syntax. */
    sizes: string;
    alt: string;
    width: number;
    height: number;
    /** Set for an image above the fold — skips lazy loading and raises priority. */
    isPriority?: boolean;
    className?: string;
    containerClass?: string;
};

/** Exported so a preload can advertise exactly the candidates the <img> offers. */
export const responsiveSrcSet = (
    name: string,
    widths: readonly number[],
    extension: 'webp' | 'jpg',
) =>
    widths
        .map(width => `/responsive/${name}-${width}.${extension} ${width}w`)
        .join(', ');

/**
 * The header banner, described once so the preload in the root layout and the
 * `<picture>` in the header cannot advertise different candidates.
 */
export const HEADER_BANNER = {
    name: 'banner_desktop',
    widths: [390, 640, 1080, 1920],
    sizes: '100vw',
    width: 3720,
    height: 610,
} as const;

const ResponsiveLocalImage = ({
    name,
    widths,
    sizes,
    alt,
    width,
    height,
    isPriority = false,
    className,
    containerClass,
}: ResponsiveLocalImageProps) => (
    <picture className={clsx('contents', containerClass)}>
        <source
            type="image/webp"
            srcSet={responsiveSrcSet(name, widths, 'webp')}
            sizes={sizes}
        />

        <img
            src={`/responsive/${name}-${widths[widths.length - 1]}.jpg`}
            srcSet={responsiveSrcSet(name, widths, 'jpg')}
            sizes={sizes}
            width={width}
            height={height}
            alt={alt}
            loading={isPriority ? 'eager' : 'lazy'}
            fetchPriority={isPriority ? 'high' : undefined}
            decoding="async"
            className={clsx('h-auto w-full', className)}
        />
    </picture>
);

export default ResponsiveLocalImage;
